import { css, html, LitElement, type TemplateResult } from "lit";
import { customElement, state } from "lit/decorators.js";
import { base as duiBase } from "@dui/primitives/core";
import { CONFIG } from "../config.ts";
import { supabase } from "../supabase.ts";

/**
 * Ends the single sign-on session in this browser, then says so. A client
 * sends a person here after forgetting its own tokens, so that its next
 * "Sign in" asks for an email again. It never redirects back on its own:
 * with no list of clients to check a return address against, a redirect
 * here would be an open one. It links to the web app, whose address is
 * fixed at build time. `local` scope: the person's sessions elsewhere stay.
 */
@customElement("sign-out-page")
export class SignOutPage extends LitElement {
  static override styles = [
    duiBase,
    css`
      :host {
        display: block;
        width: 100%;
        max-width: 24rem;
      }
      .hint {
        color: var(--muted-foreground);
        font-size: var(--text-sm);
      }
    `,
  ];

  @state()
  accessor #done = false;

  override connectedCallback(): void {
    super.connectedCallback();
    void this.#signOut();
  }

  async #signOut(): Promise<void> {
    await supabase.auth.signOut({ scope: "local" });
    this.#done = true;
  }

  /** `dui-button` with an `href` leaves navigation to the page. */
  #navigate = (e: CustomEvent<{ href: string }>): void => {
    globalThis.location.assign(e.detail.href);
  };

  override render(): TemplateResult {
    if (!this.#done) return html`<dui-spinner size="lg"></dui-spinner>`;
    return html`
      <dui-card>
        <span slot="title">You're signed out</span>
        <p class="hint">
          To use another account, go back to ETO and sign in again. Or close
          this tab.
        </p>
        <dui-button
          slot="footer"
          variant="primary"
          href=${CONFIG.webUrl}
          @dui-navigate=${this.#navigate}
        >
          Back to ETO
        </dui-button>
      </dui-card>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "sign-out-page": SignOutPage;
  }
}
